import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { EmploymentStatus } from "@/modules/hr/employee-profile/domain/types";
import { computeAge, computeGeneration, daysUntil } from "@/modules/hr/employee-profile/domain/utils";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { config } from "@/shared/lib/config";
import { formatDateLocal } from "@/shared/lib/date-time";
import { formatSalaryGradeCodeLabelDisplay } from "@/shared/lib/format-salary-grade-display";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import {
  Banknote,
  Briefcase,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarX,
  Clock,
  ExternalLink,
  Hash,
  Phone,
  Timer,
  User,
} from "lucide-react";
import { useEmployeeProfileDetailQuery } from "../api/queries";
import { EmployeePersonalInfoForm } from "../ui/EmployeePersonalInfoForm";
import { EmployeeStatusBadge } from "../ui/EmployeeStatusBadge";

export function EmployeeProfileDetailPage() {
  const { userId } = useParams({ from: "/app/hr/employees/$userId" });
  const { has } = usePermissions();
  const canEdit = has(Permissions.hr_employee_profile.upsert);
  const canReadSensitive = has(Permissions.hr_employee_profile.read_sensitive)


  const query = useEmployeeProfileDetailQuery(userId);
  const data = query.data;

  if (query.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </Main>
      </>
    );
  }

  const initials = ((data?.nameEn ?? data?.userName ?? data?.email) || "?")
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const age = data?.dateOfBirth ? computeAge(data.dateOfBirth) : null;
  const generation = data?.dateOfBirth ? computeGeneration(data.dateOfBirth) : null;

  return (
    <>
      <Header />
      <Main>
        {/* ── Header card ── */}
        <Card className="mb-6">
          <CardContent className="flex flex-wrap items-start gap-6 pt-6">
            <Avatar className="size-20 shrink-0">
              <AvatarImage src={data?.image ?? undefined} />
              <AvatarFallback className="text-xl">{initials}</AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-bold text-xl">
                  {data?.nameEn ?? data?.nameLao ?? data?.userName ?? "—"}
                </h1>
                {data?.nameLao && data.nameEn && (
                  <span className="text-muted-foreground text-sm">({data.nameLao})</span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {data?.employeeCode && (
                  <span className="flex items-center gap-1">
                    <Hash className="size-3.5" />
                    <span className="font-mono font-medium text-foreground">
                      {data.employeeCode}
                    </span>
                  </span>
                )}
                {data?.branch && (
                  <Badge variant="outline">{data.branch}</Badge>
                )}
                {data?.currentPosition?.positionTitle && (
                  <span className="flex items-center gap-1">
                    <User className="size-3.5" />
                    {data.currentPosition.positionTitle}
                  </span>
                )}
                {data?.currentPosition?.departmentName && (
                  <span className="flex items-center gap-1">
                    <Building2 className="size-3.5" />
                    {data.currentPosition.departmentName}
                  </span>
                )}
                {data?.currentPosition && (
                  <span className="w-full shrink-0 text-muted-foreground text-xs sm:w-auto">
                    ລະດັບ:{" "}
                    <span className="font-medium text-foreground">
                      {formatSalaryGradeCodeLabelDisplay(
                        data.currentPosition.salaryGradeCode,
                        data.currentPosition.salaryGradeLabel,
                      )}
                    </span>
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm">
                {data?.email && (
                  <a
                    href={`mailto:${data.email}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {data.email}
                  </a>
                )}
                {data?.phoneNumber && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Phone className="size-3.5" />
                    {data.phoneNumber}
                  </span>
                )}
                {canReadSensitive && (data?.bankAccountName || data?.bankAccountNumber) && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Banknote className="size-3.5" />
                    {data.bankAccountName || data.bankAccountNumber}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {data?.currentPosition?.startDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="size-3.5" />
                    ເລີ່ມງານ {formatDateLocal(data.currentPosition.startDate)}
                  </span>
                )}
                {age !== null && (
                  <span>
                    ອາຍຸ {age} ປີ {generation && `· ${generation}`}
                  </span>
                )}
                {(data?.documentFileKey || data?.documentFilesUrl) && (
                  <a
                    href={
                      data.documentFileKey
                        ? `${config.apiUrl}/files/${data.documentFileKey}`
                        : data.documentFilesUrl!
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    <ExternalLink className="size-3.5" />
                    ໄຟລ໌ເອກກະສານ
                  </a>
                )}
              </div>
            </div>

            <div className="shrink-0">
              {data?.status && (
                <EmployeeStatusBadge
                  status={data.status}
                  probationEndDate={data.probationEndDate}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Tabs ── */}
        <Tabs defaultValue="personal">
          <TabsList className="mb-4">
            <TabsTrigger value="personal">ຂໍ້ມູນສ່ວນຕົວ</TabsTrigger>
            <TabsTrigger value="employment">ການຈ້າງງານ</TabsTrigger>
          </TabsList>

          {/* Tab: ข้อมูลส่วนตัว */}
          <TabsContent value="personal">
            
                {canEdit ? (
                  <EmployeePersonalInfoForm
                    userId={userId}
                    data={data}
                    canEditSensitive={canReadSensitive}
                  />
                ) : (
                  <PersonalInfoReadOnly data={data} showSensitive={canReadSensitive} />
                )}
          </TabsContent>

          {/* Tab: ການຈ້າງງານ */}
          <TabsContent value="employment">
            <EmploymentTab data={data} />
          </TabsContent>
        </Tabs>
      </Main>
    </>
  );
}

// ── Employment Tab ──

const STATUS_META: Record<
  EmploymentStatus,
  { label: string; color: string; bg: string; border: string }
> = {
  active: {
    label: "ຍັງເຮັດວຽກ",
    color: "text-emerald-700 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-200 dark:border-emerald-800",
  },
  probation: {
    label: "ທົດລອງງານ",
    color: "text-amber-700 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-200 dark:border-amber-800",
  },
  probation_failed: {
    label: "ບໍ່ຜ່ານທົດລອງ",
    color: "text-orange-700 dark:text-orange-400",
    bg: "bg-orange-50 dark:bg-orange-950/40",
    border: "border-orange-200 dark:border-orange-800",
  },
  resigned: {
    label: "ລາອອກ",
    color: "text-slate-600 dark:text-slate-400",
    bg: "bg-slate-50 dark:bg-slate-900/40",
    border: "border-slate-200 dark:border-slate-700",
  },
  terminated: {
    label: "ຖືກເລີກຈ້າງ",
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-950/40",
    border: "border-red-200 dark:border-red-800",
  },
};

function computeTenure(startDate: string): string {
  const start = new Date(startDate);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) { years--; months += 12; }
  if (years > 0 && months > 0) return `${years} ປີ ${months} ເດືອນ`;
  if (years > 0) return `${years} ປີ`;
  if (months > 0) return `${months} ເດືອນ`;
  const days = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  return `${days} ວັນ`;
}

function DateRow({
  icon,
  label,
  date,
  hint,
  hintColor,
}: {
  icon: React.ReactNode;
  label: string;
  date: string | null | undefined;
  hint?: string;
  hintColor?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-0">
      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="mt-0.5 text-sm font-semibold">
          {date ? formatDateLocal(date) : "—"}
        </p>
        {hint && (
          <p className={`mt-0.5 text-xs font-medium ${hintColor ?? "text-muted-foreground"}`}>
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

function EmploymentTab({
  data,
}: {
  data: ReturnType<typeof useEmployeeProfileDetailQuery>["data"];
}) {
  if (!data) return null;

  const status = data.status ?? "active";
  const meta = STATUS_META[status];
  const startDate = data.currentPosition?.startDate ?? null;
  const tenure = startDate ? computeTenure(startDate) : null;

  const probationDays = data.probationEndDate ? daysUntil(data.probationEndDate) : null;
  const probationHint =
    probationDays !== null
      ? probationDays > 0
        ? `ເຫຼືອ ${probationDays} ວັນ`
        : probationDays === 0
        ? "ຄົບກຳນົດວັນນີ້"
        : `ເກີນກຳນົດ ${Math.abs(probationDays)} ວັນ`
      : undefined;
  const probationHintColor =
    probationDays !== null
      ? probationDays <= 0
        ? "text-red-600 dark:text-red-400"
        : probationDays <= 14
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground"
      : undefined;

  return (
    <div className="space-y-4">
      {/* Status Banner */}
      <div className={`rounded-xl border p-4 ${meta.bg} ${meta.border}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`flex size-10 items-center justify-center rounded-full bg-white/60 dark:bg-black/20 ${meta.color}`}>
              <Briefcase className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                ສະຖານະການເຮັດວຽກ
              </p>
              <p className={`text-lg font-bold ${meta.color}`}>{meta.label}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data.branch && (
              <Badge variant="outline" className="text-sm">
                {data.branch}
              </Badge>
            )}
            {data.employeeCode && (
              <Badge variant="secondary" className="font-mono text-sm">
                {data.employeeCode}
              </Badge>
            )}
          </div>
        </div>
        {status === "probation" && probationDays !== null && (
          <div className="mt-3 rounded-lg bg-white/50 dark:bg-black/20 px-3 py-2 text-sm font-medium text-amber-800 dark:text-amber-300">
            <Timer className="mb-0.5 mr-1.5 inline size-4" />
            {probationHint}
            {data.probationEndDate && ` · ຄົບ ${formatDateLocal(data.probationEndDate)}`}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Current Position */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <User className="size-4" />
              ຕຳແໜ່ງປັດຈຸບັນ
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.currentPosition ? (
              <div className="space-y-4">
                <div>
                  <p className="text-2xl font-bold leading-tight">
                    {data.currentPosition.positionTitle}
                  </p>
                  {data.currentPosition.departmentName && (
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Building2 className="size-4" />
                      {data.currentPosition.departmentName}
                    </p>
                  )}
                  <p className="mt-3 text-muted-foreground text-sm">
                    <span className="font-medium uppercase tracking-wide">
                      ລະດັບເງິນເດືອນຕຳແໜ່ງ
                    </span>
                    <span className="block font-semibold text-foreground text-base">
                      {formatSalaryGradeCodeLabelDisplay(
                        data.currentPosition.salaryGradeCode,
                        data.currentPosition.salaryGradeLabel,
                      )}
                    </span>
                  </p>
                </div>
                {tenure && (
                  <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                    <Clock className="size-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">ອາຍຸງານ</span>
                    <span className="ml-auto text-sm font-semibold">{tenure}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-muted-foreground">
                ຍັງບໍ່ມີຂໍ້ມູນຕຳແໜ່ງ
              </p>
            )}
          </CardContent>
        </Card>

        {/* Key Dates */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <Calendar className="size-4" />
              ວັນທີສຳຄັນ
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-2 pt-0">
            <DateRow
              icon={<CalendarCheck className="size-4" />}
              label="ວັນເລີ່ມງານ"
              date={startDate}
            />
            <DateRow
              icon={<Timer className="size-4" />}
              label="ວັນສິ້ນສຸດທົດລອງ"
              date={data.probationEndDate}
              hint={probationHint}
              hintColor={probationHintColor}
            />
            <DateRow
              icon={<CalendarX className="size-4" />}
              label="ວັນທີລາອອກ"
              date={data.resignationDate}
            />
          </CardContent>
        </Card>
      </div>

      {/* Notes (if any) */}
      {data.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <Hash className="size-4" />
              ໝາຍເຫດ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm">{data.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Read-only view ──
function PersonalInfoReadOnly({
  data,
  showSensitive,
}: {
  data: ReturnType<typeof useEmployeeProfileDetailQuery>["data"];
  showSensitive: boolean;
}) {
  const age = data?.dateOfBirth ? computeAge(data.dateOfBirth) : null;
  const gen = data?.dateOfBirth ? computeGeneration(data.dateOfBirth) : null;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <InfoRow label="ລະຫັດພະນັກງານ" value={data?.employeeCode} />
      <InfoRow label="ສາຂາ" value={data?.branch} />
      <InfoRow label="ຊື່ ລາວ" value={data?.nameLao} />
      <InfoRow label="ຊື່ ອັງກິດ" value={data?.nameEn} />
      <InfoRow label="ເພດ" value={data?.gender} />
      <InfoRow
        label="ວັນເກີດ"
        value={data?.dateOfBirth ? formatDateLocal(data.dateOfBirth) : null}
      />
      {age !== null && <InfoRow label="ອາຍຸ / Generation" value={`${age} ປີ · ${gen}`} />}
      <InfoRow label="ສັນຊາດ" value={data?.nationality} />
      <InfoRow label="ຊົນເຜົ່າ" value={data?.ethnicity} />
      <InfoRow label="ສາສະໜາ" value={data?.religion} />
      <InfoRow label="ກຸ່ມເລືອດ" value={data?.bloodType} />
      <InfoRow label="ສະຖານະພາບ" value={data?.maritalStatus} />
      <InfoRow
        label="ທີ່ຢູ່ເກີດ"
        value={[data?.birthVillage, data?.birthDistrict, data?.birthProvince]
          .filter(Boolean)
          .join(", ") || null}
      />
      <InfoRow
        label="ທີ່ຢູ່ປັດຈຸບັນ"
        value={[data?.currentVillage, data?.currentDistrict, data?.currentProvince]
          .filter(Boolean)
          .join(", ") || null}
      />
      {showSensitive && (
        <>
          <InfoRow label="ເລກບັດ / ສຳມະໂນ" value={data?.nationalId} />
          <InfoRow label="ຊື່ບັນຊີທະນາຄານ" value={data?.bankAccountName} />
          <InfoRow label="ເລກບັນຊີທະນາຄານ" value={data?.bankAccountNumber} />
        </>
      )}
      <InfoRow label="ໝາຍເຫດ" value={data?.notes} />
    </div>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="space-y-1">
      <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
        {label}
      </dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}
